import React, { useEffect, useState } from 'react';
import { bp_sources, bp_sentiment, bp_demographics } from '../../../services';
import CommonTabs from '../../../Components/CommonTabs';
import { Body } from '../../../Constants/Fonts';
import { formatNumber, unwrap } from './_shared';
import { langName, langTitle, platformLabel } from './LanguageAnalysis';

const VISIBLE_ROWS = 5;
const SENTIMENTS = ['negative', 'neutral', 'positive'];

const SOURCE_ORDER = ['x', 'linkedin', 'bluesky', 'facebook', 'instagram', 'youtube', 'vimeo', 'reddit'];
const SOURCE_LAST = ['forums'];
const SOURCE_EXCLUDED = ['tiktok'];
const sourceRank = v => {
    const key = v === 'twitter' ? 'x' : v;
    const i = SOURCE_ORDER.indexOf(key);
    if (i >= 0) return i;
    return SOURCE_LAST.includes(key) ? SOURCE_ORDER.length + 2 : SOURCE_ORDER.length + 1;
};
const COUNT_DEBOUNCE_MS = 300;

function useFacetCounts(filterParams) {
    const [counts, setCounts] = useState({ sources: [], sentiment: {}, languages: [] });
    useEffect(() => {
        let alive = true;
        const t = setTimeout(() => {
            const ok = r => (r.status === 'fulfilled' ? unwrap(r.value) || {} : {});
            Promise.allSettled([
                bp_sources({ ...filterParams, platform: undefined }),
                bp_sentiment({ ...filterParams, sentiment: undefined }),
                bp_demographics({ ...filterParams, language: undefined }),
            ]).then(([src, sen, lang]) => {
                if (!alive) return;
                setCounts({
                    sources: ok(src).sources || [],
                    sentiment: ok(sen).by_sentiment || {},
                    languages: ok(lang).languages || [],
                });
            });
        }, COUNT_DEBOUNCE_MS);
        return () => { alive = false; clearTimeout(t); };
    }, [filterParams]);
    return counts;
}

const VISITED_OPTIONS = [
    { key: 'all', label: 'All' },
    { key: 'unread', label: 'Unread' },
    { key: 'read', label: 'Read' },
];

function FacetGroup({ id, title, items, selected, onToggle, emptyText, footer, limit = VISIBLE_ROWS }) {
    const [open, setOpen] = useState(true);
    const [showAll, setShowAll] = useState(false);
    const activeCount = items.filter(it => selected.includes(it.value)).length;
    const visible = showAll
        ? items
        : items.filter((it, i) => i < limit || selected.includes(it.value));
    const hidden = items.length - visible.length;

    return (
        <section className="bm_facet">
            <button type="button" className="bm_facet_head" aria-expanded={open}
                aria-controls={`bm_facet_${id}`} onClick={() => setOpen(o => !o)}>
                <Body as="span" variant={2} weight="semibold" className="bm_facet_title">{title}</Body>
                {activeCount > 0 && (
                    <Body as="span" variant={2} weight="semibold" className="bm_facet_badge">{activeCount}</Body>
                )}
                <span className="bm_facet_caret" aria-hidden="true">{open ? '▾' : '▸'}</span>
            </button>
            {open && (
                <div id={`bm_facet_${id}`} className="bm_facet_body">
                    {items.length === 0 && <Body variant={2} className="bm_facet_empty">{emptyText}</Body>}
                    {visible.map(it => {
                        const on = selected.includes(it.value);
                        return (
                            <label key={it.value}
                                className={`bm_facet_row ${on ? 'is-on' : ''} ${it.count === 0 && !on ? 'is-zero' : ''}`}
                                title={it.title || it.label}>
                                <input type="checkbox" className="bm_facet_check" checked={on}
                                    onChange={() => onToggle(it.value)} />
                                {it.dot && <span className={`bm_facet_dot is-${it.dot}`} aria-hidden="true" />}
                                <Body as="span" variant={2} className="bm_facet_label">{it.label}</Body>
                                <Body as="span" variant={2} className="bm_facet_count">{formatNumber(it.count)}</Body>
                            </label>
                        );
                    })}
                    {(hidden > 0 || showAll) && items.length > limit && (
                        <button type="button" className="bm_facet_more" onClick={() => setShowAll(v => !v)}>
                            <Body as="span" variant={2} weight="semibold" className="bm_facet_more_text">
                                {showAll ? 'Show less' : `Show ${hidden} more`}
                            </Body>
                        </button>
                    )}
                    {footer}
                </div>
            )}
        </section>
    );
}

export default function MentionFilters({
    filterParams,
    visitedMode, onVisitedChange,
    knownPlatforms = [], platforms, onTogglePlatform,
    sentiments, onToggleSentiment,
    activeLanguages, onToggleLanguage, onOpenLanguageBreakdown,
    onClearRail,
}) {
    const { sources, sentiment: sentimentCounts, languages } = useFacetCounts(filterParams);
    const activeTotal = platforms.length + sentiments.length + activeLanguages.length
        + (visitedMode !== 'all' ? 1 : 0);

    const sourceItems = (sources || [])
        .map(s => ({ value: String(s.platform || '').toLowerCase(), label: platformLabel(s.platform), count: s.mentions || 0 }))
        .filter(it => !SOURCE_EXCLUDED.includes(it.value));
    const known = (knownPlatforms || [])
        .map(p => String((p && typeof p === 'object' ? (p.platform || p.value) : p) || '').toLowerCase())
        .filter(Boolean);
    [...SOURCE_ORDER, ...SOURCE_LAST, ...known, ...platforms].forEach(p => {
        const has = sourceItems.some(it => it.value === p || (p === 'x' && it.value === 'twitter')
            || (p === 'twitter' && it.value === 'x'));
        if (!has && !SOURCE_EXCLUDED.includes(p)) sourceItems.push({ value: p, label: platformLabel(p), count: 0 });
    });
    sourceItems.sort((a, b) => sourceRank(a.value) - sourceRank(b.value)
        || b.count - a.count || a.label.localeCompare(b.label));

    const sentimentItems = SENTIMENTS.map(k => ({
        value: k, label: k.charAt(0).toUpperCase() + k.slice(1),
        count: (sentimentCounts || {})[k] || 0, dot: k,
    }));

    const languageItems = (languages || [])
        .map(l => ({ value: l.lang, label: langName(l.lang), title: langTitle(l.lang), count: l.count || 0 }))
        .sort((a, b) => b.count - a.count);
    activeLanguages.forEach(l => {
        if (!languageItems.some(it => it.value === l)) languageItems.push({ value: l, label: langName(l), title: langTitle(l), count: 0 });
    });

    return (
        <div className="bm_rail_card bm_rail_filters bm_filters_panel cmn_elevated_card">
            <div className="bm_filters_head">
                <h4 className="bm_filters_title">Filters</h4>
                {activeTotal > 0 && (
                    <div className="bm_filters_status">
                        <Body as="span" variant={2} className="bm_filters_active">{activeTotal} active</Body>
                        <span className="bm_filters_sep" aria-hidden="true">·</span>
                        <button type="button" className="bm_filters_clear" onClick={onClearRail}>
                            <Body as="span" variant={2} weight="semibold" className="bm_filters_clear_text">Clear all</Body>
                        </button>
                    </div>
                )}
            </div>

            <section className="bm_facet">
                <div className="bm_facet_head is-static">
                    <Body as="span" variant={2} weight="semibold" className="bm_facet_title">Visited</Body>
                </div>
                <div className="bm_facet_body">
                    <CommonTabs
                        className="bm_visited_tabs"
                        tabs={VISITED_OPTIONS.map(o => o.label)}
                        values={VISITED_OPTIONS.map(o => o.key)}
                        active={visitedMode}
                        handlers={VISITED_OPTIONS.map(o => () => onVisitedChange(o.key))}
                    />
                </div>
            </section>

            <FacetGroup id="sources" title="Sources" items={sourceItems} limit={Infinity}
                selected={platforms} onToggle={onTogglePlatform} emptyText="No sources yet" />

            <FacetGroup id="sentiment" title="Sentiment" items={sentimentItems}
                selected={sentiments} onToggle={onToggleSentiment} emptyText="No sentiment data yet" />

            <FacetGroup id="language" title="Language" items={languageItems}
                selected={activeLanguages} onToggle={onToggleLanguage} emptyText="No language data yet"
                footer={languageItems.length > 0 && (
                    <button type="button" className="bm_facet_link" onClick={onOpenLanguageBreakdown}
                        title="Per-language mentions, platforms and latest posts">
                        <Body as="span" variant={2} weight="semibold" className="bm_facet_link_text">
                            View language breakdown →
                        </Body>
                    </button>
                )} />
        </div>
    );
}
